import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../contexts/AuthContext";
import { formatCurrency, formatDate } from "../lib/utils";
import { BarChart, FileText, Users, Clock, DollarSign, TrendingUp, Download } from "lucide-react";
import { toast } from "sonner";
import { reportsAPI, customerAPI, exportAPI } from "../lib/api";
import axios from "axios";

export function Reports() {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("subscription-renewals");

  // Subscription Reports State
  const [subscriptionReports, setSubscriptionReports] = useState({
    byService: [],
    byCustomer: [],
    byServicePeriod: []
  });
  const [subscriptionFilters, setSubscriptionFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    periodType: "month",
    periodValue: new Date().getMonth() + 1
  });

  // Interaction Reports State
  const [interactionReports, setInteractionReports] = useState({
    customerInteractions: []
  });
  const [interactionFilters, setInteractionFilters] = useState({
    customerId: "all",
    interactionType: "all",
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    fullYear: false
  });

  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);

  // Asset Reports State
  const [assetReports, setAssetReports] = useState({
    customerAssets: []
  });
  const [assetFilters, setAssetFilters] = useState({
    customerId: ""
  });

  // Pending Jobs Reports State
  const [pendingJobsReport, setPendingJobsReport] = useState([]);
  const [pendingJobsFilters, setPendingJobsFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    fullYear: false
  });

  const loadCustomersAndUsers = useCallback(async () => {
    try {
      // Use dynamic backend URL (same as api.js)
      const getBackendURL = () => {
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          return 'http://localhost:8001/api';
        }
        const currentProtocol = window.location.protocol;
        const currentHost = window.location.host;
        return `${currentProtocol}//${currentHost}/api`;
      };
      
      const [customersRes, usersRes] = await Promise.all([
        customerAPI.getAll(),
        // Note: userAPI not implemented yet, using axios with dynamic URL
        axios.get(`${getBackendURL()}/users`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` }
        })
      ]);
      
      setCustomers(customersRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error("Failed to load customers and users:", error);
      toast.error("Failed to load dropdown data");
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadCustomersAndUsers();
    }
  }, [isAdmin, loadCustomersAndUsers]);

  const generateSubscriptionReport = async (reportType) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("auth_token");
      let endpoint;
      let params = {};

      switch (reportType) {
        case "byService":
          endpoint = "/reports/subscription-renewals/by-service";
          params = { month: subscriptionFilters.month, year: subscriptionFilters.year };
          break;
        case "byCustomer":
          endpoint = "/reports/subscription-renewals/by-customer";
          params = { month: subscriptionFilters.month, year: subscriptionFilters.year };
          break;
        case "byServicePeriod":
          endpoint = "/reports/subscription-renewals/by-service-period";
          params = {
            period_type: subscriptionFilters.periodType,
            period_value: subscriptionFilters.periodValue,
            year: subscriptionFilters.periodType !== "year" ? subscriptionFilters.year : undefined
          };
          break;
      }

      // Use dynamic backend URL (same approach as api.js)
      const getBackendURL = () => {
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          return 'http://localhost:8001/api';
        }
        const currentProtocol = window.location.protocol;
        const currentHost = window.location.host;
        return `${currentProtocol}//${currentHost}/api`;
      };
      
      const response = await axios.get(`${getBackendURL()}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      setSubscriptionReports(prev => ({
        ...prev,
        [reportType]: response.data
      }));

      toast.success("Report generated successfully");
    } catch (error) {
      toast.error("Failed to generate report");
      console.error("Report generation error:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateInteractionReport = async () => {
    if (!interactionFilters.customerId) {
      toast.error("Please select a customer");
      return;
    }

    try {
      setLoading(true);
      
      // Prepare parameters - don't send month if fullYear is selected
      const month = interactionFilters.fullYear ? null : interactionFilters.month;
      const year = interactionFilters.year;
      
      const response = await reportsAPI.getInteractionsByCustomerAndType(
        interactionFilters.customerId,
        interactionFilters.interactionType,
        month,
        year
      );

      setInteractionReports({
        customerInteractions: response.data
      });

      toast.success("Interaction report generated successfully");
    } catch (error) {
      toast.error("Failed to generate interaction report");
      console.error("Interaction report generation error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssetReports = async () => {
    try {
      setLoading(true);
      
      const response = await reportsAPI.getCustomerAssets(assetFilters.customerId);

      setAssetReports({
        customerAssets: response.data.assets || []
      });

      toast.success("Asset report generated successfully");
    } catch (error) {
      toast.error("Failed to generate asset report");
      console.error("Asset report generation error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingJobsReport = async () => {
    try {
      setLoading(true);
      
      // Prepare parameters - don't send month if fullYear is selected
      const month = pendingJobsFilters.fullYear ? null : pendingJobsFilters.month;
      const year = pendingJobsFilters.year;
      
      const response = await reportsAPI.getPendingJobsReport(month, year);

      setPendingJobsReport(response.data.pending_jobs || []);

      toast.success("Pending jobs report generated successfully");
    } catch (error) {
      toast.error("Failed to generate pending jobs report");
      console.error("Pending jobs report generation error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportAssetReportPDF = async () => {
    if (!assetFilters.customerId) {
      toast.error("Please select a customer first");
      return;
    }

    try {
      const response = await exportAPI.exportAssetReportPDF(assetFilters.customerId);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `asset_report_${customers.find(c => c.id === assetFilters.customerId)?.name?.replace(' ', '_') || 'customer'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Asset report exported as PDF successfully");
    } catch (error) {
      toast.error("Failed to export PDF");
      console.error("PDF export error:", error);
    }
  };

  const handleExportAssetReportWord = async () => {
    if (!assetFilters.customerId) {
      toast.error("Please select a customer first");
      return;
    }

    try {
      const response = await exportAPI.exportAssetReportWord(assetFilters.customerId);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `asset_report_${customers.find(c => c.id === assetFilters.customerId)?.name?.replace(' ', '_') || 'customer'}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Asset report exported as Word document successfully");
    } catch (error) {
      toast.error("Failed to export Word document");
      console.error("Word export error:", error);
    }
  };
  if (!isAdmin) {
    return (
      <div className="text-center py-16">
        <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-slate-900 mb-4">Access Denied</h2>
        <p className="text-slate-600">You need admin privileges to access reports.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" data-testid="reports-title">
          Reports & Analytics
        </h1>
        <p className="text-slate-600 mt-1">Generate comprehensive business reports</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="subscription-renewals" data-testid="subscription-reports-tab">
            Subscription Reports
          </TabsTrigger>
          <TabsTrigger value="support-reports" data-testid="support-reports-tab">
            Support Reports
          </TabsTrigger>
          <TabsTrigger value="asset-reports" data-testid="asset-reports-tab">
            Asset Reports
          </TabsTrigger>
          <TabsTrigger value="pending-jobs-reports" data-testid="pending-jobs-reports-tab">
            Pending Jobs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="subscription-renewals" className="space-y-6">
          {/* Subscription Filters */}
          <Card>
            <CardHeader>
              <CardTitle>Subscription Renewal Reports</CardTitle>
              <CardDescription>Generate reports on subscription renewals by service or customer</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="sub-month">Month</Label>
                  <Select
                    value={subscriptionFilters.month.toString()}
                    onValueChange={(value) => setSubscriptionFilters({ ...subscriptionFilters, month: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>
                          {new Date(2024, i).toLocaleDateString('en-US', { month: 'long' })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="sub-year">Year</Label>
                  <Input
                    id="sub-year"
                    type="number"
                    value={subscriptionFilters.year}
                    onChange={(e) => setSubscriptionFilters({ ...subscriptionFilters, year: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <Button 
                    onClick={() => generateSubscriptionReport("byService")} 
                    disabled={loading}
                    className="mt-6"
                  >
                    By Service
                  </Button>
                </div>
                <div>
                  <Button 
                    onClick={() => generateSubscriptionReport("byCustomer")} 
                    disabled={loading}
                    className="mt-6"
                  >
                    By Customer
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Subscription Results */}
          {subscriptionReports.byService.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Renewals by Service</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {subscriptionReports.byService.map((service) => (
                    <div key={service._id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="font-semibold text-lg">{service._id}</h3>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-green-600">
                            {formatCurrency(service.total_value_aud)}
                          </p>
                          <p className="text-sm text-slate-600">{service.count} renewals</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {service.subscriptions.map((sub) => (
                          <div key={sub.id} className="flex justify-between text-sm">
                            <span>{sub.customer_name}</span>
                            <span>{formatCurrency(sub.price_aud)} ({sub.recurrence})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {subscriptionReports.byCustomer.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Renewals by Customer</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {subscriptionReports.byCustomer.map((customer) => (
                    <div key={customer._id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="font-semibold text-lg">{customer.customer_name}</h3>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-blue-600">
                            {formatCurrency(customer.total_value_aud)}
                          </p>
                          <p className="text-sm text-slate-600">{customer.count} renewals</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {customer.subscriptions.map((sub) => (
                          <div key={sub.id} className="flex justify-between text-sm">
                            <span>{sub.service_name}</span>
                            <span>{formatCurrency(sub.price_aud)} ({sub.recurrence})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="support-reports" className="space-y-6">
          {/* Interaction Filters */}
          <Card>
            <CardHeader>
              <CardTitle>Interaction Reports</CardTitle>
              <CardDescription>Filter and view all interactions by customer and type</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="interaction-customer">Select Customer *</Label>
                    <Select
                      value={interactionFilters.customerId}
                      onValueChange={(value) => setInteractionFilters({ ...interactionFilters, customerId: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a customer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Customers</SelectItem>
                        {customers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="interaction-type">Filter by Interaction Type</Label>
                    <Select
                      value={interactionFilters.interactionType}
                      onValueChange={(value) => setInteractionFilters({ ...interactionFilters, interactionType: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="phone_call">Phone Call</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="meeting">Meeting</SelectItem>
                        <SelectItem value="support">Support</SelectItem>
                        <SelectItem value="sales">Sales</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="interaction-month">Month</Label>
                    <Select
                      value={interactionFilters.month.toString()}
                      onValueChange={(value) => setInteractionFilters({ ...interactionFilters, month: parseInt(value) })}
                      disabled={interactionFilters.fullYear}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 12 }, (_, i) => (
                          <SelectItem key={i + 1} value={(i + 1).toString()}>
                            {new Date(2024, i).toLocaleDateString('en-US', { month: 'long' })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="interaction-year">Year</Label>
                    <Input
                      id="interaction-year"
                      type="number"
                      value={interactionFilters.year}
                      onChange={(e) => setInteractionFilters({ ...interactionFilters, year: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="interaction-full-year"
                    checked={interactionFilters.fullYear}
                    onChange={(e) => setInteractionFilters({ ...interactionFilters, fullYear: e.target.checked })}
                  />
                  <Label htmlFor="interaction-full-year" className="cursor-pointer">
                    Full Year (ignores month selection)
                  </Label>
                </div>
              </div>

              <Button 
                onClick={generateInteractionReport}
                disabled={loading || !interactionFilters.customerId}
                data-testid="generate-interaction-report-btn"
              >
                {loading ? "Generating..." : "Generate Interaction Report"}
              </Button>
            </CardContent>
          </Card>

          {interactionReports.customerInteractions && interactionReports.customerInteractions.interactions && interactionReports.customerInteractions.interactions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Interaction Report</CardTitle>
                <CardDescription>
                  {interactionReports.customerInteractions.filter_type === "all" 
                    ? "All interactions" 
                    : `${interactionReports.customerInteractions.filter_type.replace('_', ' ')} interactions`
                  } for {interactionReports.customerInteractions.customer_name}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">
                        {interactionReports.customerInteractions.total_interactions}
                      </div>
                      <div className="text-sm text-blue-600">Total Interactions</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {Math.round((interactionReports.customerInteractions.total_duration_minutes || 0) / 60 * 10) / 10}h
                      </div>
                      <div className="text-sm text-green-600">Total Duration</div>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <div className="text-2xl font-bold text-orange-600">
                        {interactionReports.customerInteractions.pending_jobs_count}
                      </div>
                      <div className="text-sm text-orange-600">Pending Jobs</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">
                        {interactionReports.customerInteractions.interactions.filter(i => i.type === 'phone_call').length}
                      </div>
                      <div className="text-sm text-purple-600">Phone Calls</div>
                    </div>
                  </div>

                  {/* Interactions Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left p-2">Type</th>
                          <th className="text-left p-2">Date</th>
                          <th className="text-left p-2">Duration</th>
                          <th className="text-left p-2">User</th>
                          <th className="text-left p-2">Status</th>
                          <th className="text-left p-2">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {interactionReports.customerInteractions.interactions.map((interaction, index) => (
                          <tr key={interaction.id} className={index % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                            <td className="p-2">
                              <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                                interaction.type === 'phone_call' ? 'bg-green-100 text-green-800' :
                                interaction.type === 'email' ? 'bg-blue-100 text-blue-800' :
                                interaction.type === 'meeting' ? 'bg-purple-100 text-purple-800' :
                                interaction.type === 'support' ? 'bg-orange-100 text-orange-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {interaction.type.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="p-2">{formatDate(interaction.date)}</td>
                            <td className="p-2">{interaction.duration_minutes ? `${interaction.duration_minutes} min` : "-"}</td>
                            <td className="p-2">{interaction.user_name}</td>
                            <td className="p-2">
                              {interaction.pending_job ? (
                                <span className="inline-flex px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                                  Pending
                                </span>
                              ) : (
                                <span className="inline-flex px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                                  Complete
                                </span>
                              )}
                            </td>
                            <td className="p-2 max-w-xs truncate">{interaction.notes || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="asset-reports" className="space-y-6">
          {/* Asset Filters */}
          <Card>
            <CardHeader>
              <CardTitle>Asset Reports</CardTitle>
              <CardDescription>Generate reports on assets by customer</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label htmlFor="asset-customer">Select Customer</Label>
                  <Select 
                    value={assetFilters.customerId} 
                    onValueChange={(value) => setAssetFilters({ ...assetFilters, customerId: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    onClick={fetchAssetReports}
                    disabled={loading || !assetFilters.customerId}
                    data-testid="generate-asset-report-btn"
                  >
                    {loading ? "Generating..." : "Generate Asset Report"}
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={handleExportAssetReportPDF}
                    disabled={loading || !assetFilters.customerId}
                    data-testid="export-asset-report-pdf-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export PDF
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={handleExportAssetReportWord}
                    disabled={loading || !assetFilters.customerId}
                    data-testid="export-asset-report-word-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Word
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Asset Report Results */}
          {assetReports.customerAssets.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Customer Assets Report</CardTitle>
                <CardDescription>
                  Assets for {customers.find(c => c.id === assetFilters.customerId)?.name}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">
                        {assetReports.customerAssets.length}
                      </div>
                      <div className="text-sm text-blue-600">Total Assets</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {assetReports.customerAssets.filter(asset => asset.asset_type).length}
                      </div>
                      <div className="text-sm text-green-600">Categorized</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">
                        {assetReports.customerAssets.filter(asset => asset.user).length}
                      </div>
                      <div className="text-sm text-purple-600">Assigned</div>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left p-2">Asset Name</th>
                          <th className="text-left p-2">Type</th>
                          <th className="text-left p-2">Location</th>
                          <th className="text-left p-2">Assigned User</th>
                          <th className="text-left p-2">Serial Number</th>
                          <th className="text-left p-2">Purchase Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assetReports.customerAssets.map((asset, index) => (
                          <tr key={asset.id} className={index % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                            <td className="p-2 font-medium">{asset.name}</td>
                            <td className="p-2">{asset.asset_type || "-"}</td>
                            <td className="p-2">{asset.location || "-"}</td>
                            <td className="p-2">{asset.user || "-"}</td>
                            <td className="p-2 font-mono text-xs">{asset.serial_number || "-"}</td>
                            <td className="p-2">{asset.purchase_date ? formatDate(asset.purchase_date) : "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="pending-jobs-reports" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Pending Jobs Report</CardTitle>
              <CardDescription>View all pending jobs from all users</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="pending-month">Month</Label>
                  <Select
                    value={pendingJobsFilters.month.toString()}
                    onValueChange={(value) => setPendingJobsFilters({ ...pendingJobsFilters, month: parseInt(value) })}
                    disabled={pendingJobsFilters.fullYear}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>
                          {new Date(2024, i).toLocaleDateString('en-US', { month: 'long' })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="pending-year">Year</Label>
                  <Input
                    id="pending-year"
                    type="number"
                    value={pendingJobsFilters.year}
                    onChange={(e) => setPendingJobsFilters({ ...pendingJobsFilters, year: parseInt(e.target.value) })}
                  />
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={fetchPendingJobsReport}
                    disabled={loading}
                    data-testid="generate-pending-jobs-report-btn"
                  >
                    {loading ? "Generating..." : "Generate Report"}
                  </Button>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="pending-full-year"
                  checked={pendingJobsFilters.fullYear}
                  onChange={(e) => setPendingJobsFilters({ ...pendingJobsFilters, fullYear: e.target.checked })}
                />
                <Label htmlFor="pending-full-year" className="cursor-pointer">
                  Full Year (ignores month selection)
                </Label>
              </div>
            </CardContent>
          </Card>

          {pendingJobsReport.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>All Pending Jobs</CardTitle>
                <CardDescription>
                  Complete list of pending jobs across all users
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <div className="text-2xl font-bold text-orange-600">
                        {pendingJobsReport.length}
                      </div>
                      <div className="text-sm text-orange-600">Total Pending Jobs</div>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">
                        {pendingJobsReport.filter(job => job.is_onsite_support).length}
                      </div>
                      <div className="text-sm text-blue-600">Onsite Jobs</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {pendingJobsReport.filter(job => !job.is_onsite_support).length}
                      </div>
                      <div className="text-sm text-green-600">Remote Jobs</div>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left p-2">Customer</th>
                          <th className="text-left p-2">Type</th>
                          <th className="text-left p-2">Date</th>
                          <th className="text-left p-2">Assigned User</th>
                          <th className="text-left p-2">Support Type</th>
                          <th className="text-left p-2">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingJobsReport.map((job, index) => (
                          <tr key={job.id} className={index % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                            <td className="p-2 font-medium">{job.customer_name}</td>
                            <td className="p-2">
                              <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                                job.type === 'phone_call' ? 'bg-green-100 text-green-800' :
                                job.type === 'email' ? 'bg-blue-100 text-blue-800' :
                                job.type === 'meeting' ? 'bg-purple-100 text-purple-800' :
                                job.type === 'support' ? 'bg-orange-100 text-orange-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {job.type.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="p-2">{formatDate(job.date)}</td>
                            <td className="p-2">{job.user_name}</td>
                            <td className="p-2">
                              {job.is_onsite_support ? (
                                <span className="inline-flex px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                                  Onsite
                                </span>
                              ) : (
                                <span className="inline-flex px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                                  Remote
                                </span>
                              )}
                            </td>
                            <td className="p-2 max-w-xs truncate">{job.notes || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}